<template>
    <cr-form @submit="onSubmit" class="cr-mb6">
        <fieldset>
            <legend class="cr-mb4">{{ challenge.description }}</legend>
            <cr-textarea v-model="message" required :rows="8" :label="t('CHALLENGE_BOT_ARTIFACT_LABEL_CAT_GUARD')" :disabled="loadingResponse"></cr-textarea>
        </fieldset>
        <cr-button type="submit" background-color="cr-primary-button-color" text-color="cr-primary-button-text-color" class="cr-mr2 cr-mtb3" :disabled="loadingResponse">Send</cr-button>
        <cr-loading v-if="loadingResponse" color="cr-primary-button-color" :message="t('CHALLENGE_BOT_ARTIFACT_THINKING')" ref="loading"/>
        <p v-else-if="response" ref="response" class="response" data-testing="bot-artifact-response"><cr-type-writer :text="response" /></p>
    </cr-form>
</template>

<style scoped>
:deep(textarea),
:deep(label)::after
{
    background-color: transparent !important;
}
:deep(label)::before
{
    content: none !important;
}
.cr-textarea,
.cr-textarea :deep(textarea)
{
    font-size: var(--cr-input-text-size);
}
.cr-button
{
    border-radius: var(--cr-pill-button-border-radius);
}
.cr-loading, .response {
    scroll-margin-bottom: 2rem;
}
</style>

<script setup lang="ts">
import { IChallenge } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { useI18n } from 'vue-i18n';
import { useChallengeStore } from '../../stores/challengeStore';
import { nextTick, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useAnnouncementStore } from '@cyber-range/cyber-range-lib-ui';

const announncementStore = useAnnouncementStore();

const { t } = useI18n();

const props = defineProps<{
    challenge: IChallenge;
    resetKey?: number;
}>();

const message = ref('');

const loadingResponse = ref(false);
const response = ref('');

watch(() => props.resetKey, () =>
{
    message.value = '';
    response.value = '';
});

const loadingRef = useTemplateRef('loading');
const responseRef = useTemplateRef('response');
let responseResizeObserver: ResizeObserver|undefined;
watch(loadingResponse, async (value) =>
{
    if(value) {
        await nextTick();
        loadingRef.value?.$el?.scrollIntoView({ block: 'nearest' });
    }
    else if (response.value)
    {
        await nextTick();
        if (!responseRef.value) return

        responseResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                entry.target.scrollIntoView({ block: 'nearest' });
            }
        });
        responseResizeObserver.observe(responseRef.value)
    }
});

watch(response, async (value) =>
{
    if (!value)
    {
        responseResizeObserver?.disconnect();
    }
});
onUnmounted(() => { responseResizeObserver?.disconnect(); })

async function onSubmit()
{
    announncementStore.announce(t('CHALLENGE_BOT_ARTIFACT_THINKING'));
    loadingResponse.value = true;   
    try
    {
        // pretend the ai is thinking for at least 1 second, but don't go longer that that if it actually takes longer
        const [result, ] = await Promise.all([
            response.value = await useChallengeStore().submitBotArtifactMessage(props.challenge.id, message.value),
            new Promise(res => setTimeout(res, 1000))
        ]);
        response.value = result;
    }
    finally
    {
        loadingResponse.value = false;
    }
    announncementStore.announce(t('CHALLENGE_BOT_ARTIFACT_THINKING_DONE', {message: response.value}));
}
</script>
